/**
 * WHEN READY TO RE-IMPLEMENT CHECKBOX SELECTIONS AND BULK ACTIONS
 * REFER TO THIS COMMIT: https://github.com/ankor-io/pay/pull/547/commits/b6d041fc4c4b8050b9c97bc7ae3adc59049fba46
 */
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Check, Ellipsis, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router'

import { isApproverOrHigherRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import {
  AWXCardActivityEventStatus,
  PaymentMethod,
  type TransactionPayload,
} from '@ankor-io/common/types/transaction/transaction'
import { type Transaction, TransactionStatus } from '@ankor-io/common/types/transaction/transaction'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDate } from '@ankor-io/common/util/dates'
import { getPaymentMethodText } from '@ankor-io/common/util/enum-to-string/payment.method'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/transaction'
import {
  getPaymentStatusColour,
  getPaymentStatusIcon,
  getPaymentStatusText,
} from '@ankor-io/common/util/enum-to-string/transaction.payment.status'

import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Chip } from '@/ui-components/Chip'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'

type CharterTransactionsTableProps = {
  transactions: TransactionPayload[]
  onUpdatedTransactions: (transactions: Transaction[]) => void
}

const CharterTransactionsTable = ({ transactions, onUpdatedTransactions }: CharterTransactionsTableProps) => {
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole, user } = useUserContext()

  const [isActioningTransaction, setIsActioningTransaction] = useState(false)

  const handleApprove = (transaction: Transaction) => {
    const approveTransaction = async () => {
      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsActioningTransaction(true)

      let baseUrl = `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}`
      if (transaction.charterId) {
        baseUrl = `${baseUrl}/charter/${transaction.charterId}`
      }

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(`${baseUrl}/transaction/${transaction.id}/status`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          method: 'POST',
          body: JSON.stringify({ status: TransactionStatus.APPROVED }),
        })

        if (!res.ok) {
          throw new Error(`Failed to approve transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.APPROVED }
        const updateTransactions = transactions.map((t) => (t.id === transaction.id ? updatedTransaction : t))
        onUpdatedTransactions(updateTransactions)
      } catch (error) {
        console.error('Error approving transactions:', error)
        throw new Error(`Failed to approve transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(approveTransaction, {
      loading: 'Approving expense...',
      success: 'Expense approved successfully.',
      error: 'Failed to approve expense.',
    })
  }

  const handleReject = (transaction: Transaction) => {
    const rejectTransaction = async () => {
      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsActioningTransaction(true)

      let baseUrl = `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}`
      if (transaction.charterId) {
        baseUrl = `${baseUrl}/charter/${transaction.charterId}`
      }

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const res = await fetch(`${baseUrl}/transaction/${transaction.id}/status`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          method: 'POST',
          body: JSON.stringify({ status: TransactionStatus.REJECTED }),
        })

        if (!res.ok) {
          throw new Error(`Failed to reject transaction.`)
        }

        const updatedTransaction = { ...transaction, status: TransactionStatus.REJECTED }
        const updateTransactions = transactions.map((t) => (t.id === transaction.id ? updatedTransaction : t))
        onUpdatedTransactions(updateTransactions)
      } catch (error) {
        console.error('Error rejecting transactions:', error)
        throw new Error(`Failed to reject transaction.`)
      } finally {
        setIsActioningTransaction(false)
      }
    }

    toast.promise(rejectTransaction, {
      loading: `Rejecting expense...`,
      success: `Expense rejected successfully.`,
      error: `Failed to reject expense.`,
    })
  }

  const handleEditTransaction = (transaction: TransactionPayload) => {
    navigate(`/transactions/${transaction.id}/edit`)
  }

  return (
    <div className="overflow-x-auto">
      <Table className="max-w-[calc(100vw-7rem)] lg:max-w-[calc(100vw-24.1rem)]">
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Payment Status</TableHeader>
            <TableHeader>Category</TableHeader>
            <TableHeader>Payment Method</TableHeader>
            <TableHeader>Total</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow
              className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              key={transaction.id}
              to={`/transactions/${transaction.id}`}
            >
              {/* Date */}
              <TableCell>{formatDate(transaction.date)}</TableCell>

              {/* Status */}
              <TableCell>
                <Badge color={getStatusColour(transaction.status)}>{getStatusText(transaction.status)}</Badge>
              </TableCell>

              {/* Payment Status */}
              <TableCell>
                {/* TODO: Show payment status for transfers? */}
                {transaction.paymentMethod === PaymentMethod.ANKOR_PAY && transaction.awxActivityEvent && (
                  <Chip
                    colour={getPaymentStatusColour(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                    name={getPaymentStatusText(transaction.awxActivityEvent.status as AWXCardActivityEventStatus)}
                    icon={getPaymentStatusIcon(transaction.awxActivityEvent.type)}
                  />
                )}
              </TableCell>

              {/* Category */}
              <TableCell>
                {transaction.category && (
                  <Chip
                    name={transaction.category.name}
                    colour={transaction.category.color}
                    icon={transaction.category.icon}
                  />
                )}
              </TableCell>

              {/* Payment Method */}
              <TableCell>{getPaymentMethodText(transaction.paymentMethod)}</TableCell>

              {/* Total */}
              <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                {formatCurrency(fromCents(transaction.amountCents), transaction.currencyCode)}
              </TableCell>

              {/* Actions */}
              <TableCell>
                {/* Is Admin or Owner, can always action */}
                {/* Is Spender or Approver, can only action their own transactions that are not approved */}
                {(isPrivilegedRole(activeOrganizationRole) ||
                  ((activeOrganizationRole === Role.SPENDER || activeOrganizationRole === Role.APPROVER) &&
                    transaction.createdBy === user?.id &&
                    transaction.status !== TransactionStatus.APPROVED)) && (
                  <div
                    className="relative z-10 pointer-events-auto"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Dropdown>
                      <DropdownButton plain>
                        <Ellipsis data-slot="icon" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem
                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isActioningTransaction}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            handleEditTransaction(transaction)
                          }}
                        >
                          <Pencil data-slot="icon" />
                          Edit
                        </DropdownItem>
                        {transaction.status !== TransactionStatus.APPROVED &&
                          transaction.status !== TransactionStatus.REJECTED &&
                          isApproverOrHigherRole(activeOrganizationRole) && (
                            <>
                              <DropdownItem
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isActioningTransaction}
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation()
                                  handleApprove(transaction)
                                }}
                              >
                                <Check data-slot="icon" />
                                Approve
                              </DropdownItem>
                              <DropdownItem
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isActioningTransaction}
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation()
                                  handleReject(transaction)
                                }}
                              >
                                <X data-slot="icon" />
                                Reject
                              </DropdownItem>
                            </>
                          )}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default CharterTransactionsTable
